import { useState, useMemo } from 'react';
import { Link, Outlet } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { MapPin, Phone, PlusCircle, Shield, User } from 'lucide-react';

import { useGetUserProfile } from '@/hooks/api/use-get-user-profile';
import { useGetApiKeys } from '@/hooks/api/use-get-api-keys';
import { toast_warning } from '@/libs/toast-wrappers';
import { ROUTES } from '@/router/routes';
import { getInitials } from '@/utils/auth';
import { EMPTY_VALUE } from '@/utils/strings';

import { NewLicenseForm } from '@/components/profile/new-license-form';
import { ApiKeyManagement } from '@/components/admin/users/api-key-management';
import { Table } from '@/components/ui/table';
import { Hr } from '@/components/ui/divider';
import { Banner } from '@/components/ui/banner';
import { FetchStates } from '@/components/fetch-states';
import { Section } from '@/components/content-layout/section';
import { SectionHeader } from '@/components/content-layout/section-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

export function ProfilePage() {
  // refresh the user profile
  const { profile, loading: loadingProfile, refresh: refreshProfile } = useGetUserProfile();
  const { apiKeys } = useGetApiKeys(profile?.email ?? null);

  const [addingNewLicense, setAddingNewLicense] = useState(false);

  const { t } = useTranslation();

  const isGraderUser = useMemo(
    () => profile?.role === 'telehealthclinician' || profile?.role === 'testgrader',
    [profile]
  );
  const isTwoFactorAuthEnabled = profile?.two_factor_auth_enabled;

  const onFinishSaveLicense = () => {
    setAddingNewLicense(false);
    setTimeout(() => {
      void refreshProfile();
    }, 500);
  };

  const onFormClose = async () => {
    await refreshProfile();
  };

  // country, state, license #, expiration, license upload, and approved
  const licensesTableHeaders = useMemo(
    () => [
      { key: 'license_number', label: 'License #' },
      { key: 'state_province', label: 'License State' },
      { key: 'expiration', label: 'Expiration Date' },
      { key: 'request_state', label: 'State', type: 'request-state' as const },
      {
        key: 'actions',
        type: 'actions' as const,
        edit: () => toast_warning('Edit licenses will be here soon!'),
        delete: () => toast_warning('Delete licenses will be here soon!'),
      },
    ],
    []
  );

  return (
    <>
      <Section>
        <SectionHeader title={t('profile-title')} element="h1">
          <p className="font-bold">
            {profile?.email}
            {profile?.clinic_name ? ` - ${profile.clinic_name}` : ''}
          </p>
        </SectionHeader>
        <FetchStates loading={loadingProfile}>
          {!isTwoFactorAuthEnabled ? (
            <div className="my-2">
              <Banner type="warning" enableClose={false}>
                <div className="flex items-center gap-2">
                  <div>
                    <p className="font-bold">{t('two-factor-auth-inactive')}</p>
                    <p>{t('two-factor-auth-inactive-description')}</p>
                  </div>
                  <Button
                    href={ROUTES.TWO_FACTOR_AUTH_ENABLE}
                    variant="outline"
                    className="small ml-auto"
                  >
                    <span className=" font-bold">{t('two-factor-auth-enable-button')}</span>
                  </Button>
                </div>
              </Banner>
            </div>
          ) : null}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t('profile-personal-information')}
                </CardTitle>
                <CardDescription>{t('profile-personal-information-description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 pb-4">
                  <Avatar className="h-16 w-16 rounded-lg">
                    <AvatarFallback className="rounded-lg">{getInitials(profile)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{profile?.full_name}</h3>
                    <ProfileField fieldValue={profile?.clinic_name} />
                  </div>
                </div>
                <Hr />
                <div className="flex justify-between items-center">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      {t('profile-email-address')}
                    </label>
                    <ProfileField fieldValue={profile?.email} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      {t('profile-phone-number')}
                    </label>
                    <p className="text-gray-900 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <ProfileField fieldValue={profile?.contact_phone} />
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Security Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  {t('profile-security-settings')}
                </CardTitle>
                <CardDescription>{t('profile-security-settings-description')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{t('profile-two-factor-auth-title')}</p>
                    <p className="text-sm text-gray-500">
                      {t('profile-two-factor-auth-description')}
                    </p>
                    {isTwoFactorAuthEnabled ? (
                      <p className=" text-xs italic text-gray-500 mt-2">
                        {`${t('profile-two-factor-auth-disable-link')}`}
                        <Link className="underline mx-1" to={ROUTES.TWO_FACTOR_AUTH_DISABLE}>
                          {t('profile-two-factor-auth-disable-link-text')}
                        </Link>
                        {`${t('profile-two-factor-auth-disable-link-text-2')}`}
                      </p>
                    ) : null}
                  </div>
                  <Badge color={isTwoFactorAuthEnabled ? 'green' : 'red'}>
                    {isTwoFactorAuthEnabled
                      ? t('profile-two-factor-auth-enabled')
                      : t('profile-two-factor-auth-disabled')}
                  </Badge>
                </div>
                <Hr />
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  {t('profile-address-information')}
                </CardTitle>
                <CardDescription>{t('profile-address-information-description')}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('profile-country')}
                      </label>
                      <ProfileField fieldValue={profile?.country} />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          {t('profile-city')}
                        </label>
                        <ProfileField fieldValue={profile?.city} />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          {t('profile-postal-code')}
                        </label>
                        <ProfileField fieldValue={profile?.postal_code} />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('profile-province-state')}
                      </label>
                      <ProfileField fieldValue={profile?.province_or_state} />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        {t('profile-street-address')}
                      </label>
                      <ProfileField fieldValue={profile?.street_address} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </FetchStates>
      </Section>

      {isGraderUser && !loadingProfile && (
        <>
          <Hr />
          <Section>
            <SectionHeader title={t('profile-licenses-title')}>
              {!addingNewLicense && (
                <Button type="button" onClick={() => setAddingNewLicense(true)}>
                  <span>{t('profile-add-license')}</span>
                  <PlusCircle />
                </Button>
              )}
            </SectionHeader>
            {addingNewLicense && (
              <div className="mb-12">
                <NewLicenseForm onSuccessSave={onFinishSaveLicense} />
              </div>
            )}
            <Table headers={licensesTableHeaders} data={profile?.licenses ?? []} />
          </Section>
        </>
      )}

      {/* API Keys Section */}
      {profile?.email && !loadingProfile && apiKeys.length > 0 && (
        <>
          <Hr />
          <Section>
            <Card>
              <CardContent>
                <ApiKeyManagement userEmail={profile.email} readOnly />
              </CardContent>
            </Card>
          </Section>
        </>
      )}

      <Outlet
        context={
          { onFormClose } satisfies {
            onFormClose: () => Promise<void>;
          }
        }
      />
    </>
  );
}

const ProfileField = ({ fieldValue }: { fieldValue?: string }) => {
  if (fieldValue) return <p className="text-gray-500">{fieldValue}</p>;
  return <p className="text-gray-500 italic text-sm">{EMPTY_VALUE}</p>;
};
